import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar as CalendarIcon, Clock, ChevronLeft, ChevronRight, X, Check, Loader2 } from "lucide-react";
import { Button } from "./ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTH_NAMES = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const DAY_SHORT = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

function isoDate(d) {
  // Format YYYY-MM-DD en heure locale (évite le décalage UTC qui shifte d'un jour)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function dateLabel(d) {
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
}

/**
 * Modale de reprogrammation d'un rendez-vous existant.
 * Reprend exactement le même UX que la BookingPage (calendrier + créneaux).
 *
 * Props:
 *  - appointment: l'objet appointment complet (avec service_id, variation_id, etc.)
 *  - onClose: fermeture
 *  - onSuccess: appelé après reprogrammation OK
 */
export default function RescheduleModal({ appointment, onClose, onSuccess }) {
  const today = useMemo(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  }, []);
  const [calendarDate, setCalendarDate] = useState(() => {
    const d = new Date(appointment.date + "T00:00:00");
    d.setHours(0, 0, 0, 0);
    return d.getTime() < today.getTime() ? new Date(today) : d;
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Génération de la grille du mois affiché
  const monthGrid = useMemo(() => {
    const y = calendarDate.getFullYear();
    const m = calendarDate.getMonth();
    const first = new Date(y, m, 1);
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const startOffset = (first.getDay() + 6) % 7; // Lundi = 0
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m, d));
    return cells;
  }, [calendarDate]);

  // Récupérer les créneaux quand une date est cliquée
  useEffect(() => {
    if (!selectedDate || !appointment.service_id) return;
    let cancelled = false;
    const fetchSlots = async () => {
      setSlotsLoading(true);
      try {
        const params = { date: isoDate(selectedDate), service_id: appointment.service_id };
        if (appointment.variation_id) params.variation_id = appointment.variation_id;
        const res = await axios.get(`${API}/appointments/available-slots`, { params });
        if (cancelled) return;
        setSlots(res.data.available_slots || []);
        setSelectedSlot(null);
      } catch (e) {
        if (!cancelled) toast.error("Erreur de chargement des créneaux");
      } finally {
        if (!cancelled) setSlotsLoading(false);
      }
    };
    fetchSlots();
    return () => { cancelled = true; };
  }, [selectedDate, appointment.service_id, appointment.variation_id]);

  const handleConfirm = async () => {
    if (!selectedDate || !selectedSlot) {
      toast.error("Choisis une date et un créneau");
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/appointments/${appointment.appointment_id}/reschedule`,
        { date: isoDate(selectedDate), time_slot: selectedSlot },
        { withCredentials: true }
      );
      toast.success("Rendez-vous reprogrammé ✨");
      if (onSuccess) onSuccess();
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Échec de la reprogrammation");
    } finally {
      setSaving(false);
    }
  };

  const isPast = (d) => d.getTime() < today.getTime();
  const isSelected = (d) => selectedDate && isoDate(d) === isoDate(selectedDate);
  const isOriginal = (d) => isoDate(d) === appointment.date;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        data-testid="reschedule-modal"
      >
        <motion.div
          initial={{ scale: 0.94, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.94, opacity: 0 }}
          className="relative w-full max-w-3xl bg-[#261B3B] border border-white/15 rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          <button
            onClick={onClose}
            className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-black/30 hover:bg-black/50 transition-colors"
            data-testid="reschedule-close"
          >
            <X className="w-4 h-4 text-white" />
          </button>

          {/* Header */}
          <div className="p-5 border-b border-white/10 bg-gradient-to-r from-[#3A115E]/40 to-transparent">
            <div className="flex items-center gap-2 mb-1">
              <CalendarIcon className="w-5 h-5 text-[#D926A9]" />
              <h2 className="font-serif text-xl text-[#F7F3FC]">Reprogrammer le rendez-vous</h2>
            </div>
            <p className="text-sm text-[#B59ED1]">
              <span className="text-[#E2D5F8]">{appointment.service_name || "Prestation"}</span>
              {" · "}
              actuellement le {new Date(appointment.date + "T00:00:00").toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} à {appointment.time_slot}
            </p>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-5 grid md:grid-cols-2 gap-6">
            {/* Calendrier */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <button
                  onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1))}
                  className="p-1.5 rounded hover:bg-white/10"
                  data-testid="reschedule-prev-month"
                >
                  <ChevronLeft className="w-4 h-4 text-[#B59ED1]" />
                </button>
                <h3 className="font-serif text-base text-[#F7F3FC]">
                  {MONTH_NAMES[calendarDate.getMonth()]} {calendarDate.getFullYear()}
                </h3>
                <button
                  onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1))}
                  className="p-1.5 rounded hover:bg-white/10"
                  data-testid="reschedule-next-month"
                >
                  <ChevronRight className="w-4 h-4 text-[#B59ED1]" />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-2">
                {DAY_SHORT.map((d, i) => (
                  <div key={i} className="text-center text-[10px] uppercase tracking-wider text-[#B59ED1] py-1">{d}</div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {monthGrid.map((d, idx) => {
                  if (!d) return <div key={`empty-${idx}`} />;
                  const past = isPast(d);
                  const sel = isSelected(d);
                  const orig = isOriginal(d);
                  const dayKey = isoDate(d);
                  return (
                    <button
                      key={dayKey}
                      disabled={past}
                      onClick={() => setSelectedDate(d)}
                      className={`aspect-square rounded-lg text-sm font-medium transition-all ${
                        past
                          ? "text-[#3A2952] cursor-not-allowed"
                          : sel
                            ? "bg-[#D926A9] text-white shadow-lg scale-105"
                            : orig
                              ? "bg-[#3A115E]/60 text-[#E2D5F8] border border-[#D926A9]/40"
                              : "text-[#E2D5F8] hover:bg-white/10"
                      }`}
                      data-testid={`reschedule-day-${isoDate(d)}`}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>

              {selectedDate && (
                <p className="text-xs text-[#00E5FF] mt-3 capitalize">→ {dateLabel(selectedDate)}</p>
              )}
            </div>

            {/* Créneaux */}
            <div>
              <h3 className="font-serif text-base text-[#F7F3FC] mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#00E5FF]" /> Créneaux disponibles
              </h3>

              {!selectedDate && (
                <p className="text-sm text-[#B59ED1] italic">Choisis d'abord une date sur le calendrier.</p>
              )}

              {selectedDate && slotsLoading && (
                <div className="text-center py-8 text-[#B59ED1]">
                  <Loader2 className="w-5 h-5 mx-auto animate-spin" />
                  <p className="text-xs mt-2">Chargement des créneaux…</p>
                </div>
              )}

              {selectedDate && !slotsLoading && slots.length === 0 && (
                <p className="text-sm text-amber-400 bg-amber-400/10 border border-amber-400/30 rounded-lg p-3">
                  Aucun créneau libre ce jour. Essaie une autre date.
                </p>
              )}

              {selectedDate && !slotsLoading && slots.length > 0 && (
                <div className="grid grid-cols-3 gap-2 max-h-[280px] overflow-y-auto pr-1">
                  {slots.map(s => (
                    <button
                      key={s}
                      onClick={() => setSelectedSlot(s)}
                      className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                        selectedSlot === s
                          ? "bg-[#D926A9] text-white shadow-lg"
                          : "bg-[#1B132B] text-[#E2D5F8] hover:bg-[#D926A9]/20 border border-white/10"
                      }`}
                      data-testid={`reschedule-slot-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-white/10 p-4 flex items-center justify-between gap-3 bg-[#1B132B]/40">
            <p className="text-xs text-[#B59ED1]">
              {selectedDate && selectedSlot
                ? <>Nouveau : <span className="text-[#F7F3FC]">{dateLabel(selectedDate)} à {selectedSlot}</span></>
                : "Aucun nouveau créneau choisi"}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="border-white/15" data-testid="reschedule-cancel">
                Annuler
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={!selectedDate || !selectedSlot || saving}
                className="gradient-primary"
                data-testid="reschedule-confirm"
              >
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reprogrammation…</> : <><Check className="w-4 h-4 mr-2" /> Confirmer</>}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
